import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import {
  GetUserNavigationSettingsResponse,
  NavigationLayout,
  NavigationLayoutSource,
  NavigationManagementMode,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { getUserNavigationSettings } from "api/endpoints/navigation";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { UserUrlParamTypes } from "types/user-display";

type Platform = "web" | "mobile";

type UserNavigationTabProps = {
  orgId?: string;
};

const DISCLAIMER =
  "Items may also be hidden by user permissions, feature flags, training mode, or seat-permission checks. In user-managed mode, users may have personalised their navigation; this view shows the org-side configuration only.";

const managementModeLabel = (
  mode: NavigationManagementMode | undefined
): string => {
  switch (mode) {
    case NavigationManagementMode.ORG_MANAGED:
      return "Org-managed";
    case NavigationManagementMode.USER_MANAGED:
      return "User-managed";
    default:
      return "Unspecified";
  }
};

const managementModeColor = (
  mode: NavigationManagementMode | undefined
): "primary" | "warning" | "default" => {
  switch (mode) {
    case NavigationManagementMode.ORG_MANAGED:
      return "primary";
    case NavigationManagementMode.USER_MANAGED:
      return "warning";
    default:
      return "default";
  }
};

const layoutTypeLabel = (
  source: NavigationLayoutSource,
  permissionSetName?: string,
  permissionSetId?: string
): string => {
  if (source === NavigationLayoutSource.ROLE_BASED) {
    const ref = permissionSetName || permissionSetId || "(unknown)";
    return `Role-based — "${ref}"`;
  }
  if (source === NavigationLayoutSource.ORG_DEFAULT) {
    return "Org default";
  }
  return "Unknown";
};

// stripNavigationItemPrefix turns NAVIGATION_ITEM_TYPE_TEMPLATES into "Templates"
// for display. The id arrives from the backend as the upstream enum name; the
// FE doesn't carry the full enum locally so this string transform is the
// stable rendering choice.
const stripNavigationItemPrefix = (id: string): string => {
  const stripped = id.replace(/^NAVIGATION_ITEM_TYPE_/, "");
  if (!stripped) {
    return id;
  }
  return stripped
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
};


const renderLayoutTable = (
  platform: Platform,
  layout: NavigationLayout | undefined
) => {
  const items = layout?.items ?? [];
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        {platform === "web" ? "Web" : "Mobile"} layout
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Item</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                Primary
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                Hidden
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No items configured.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={`${platform}-${item.id}`}>
                  <TableCell>
                    <Tooltip title={item.id}>
                      <span>{stripNavigationItemPrefix(item.id)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" data-testid={`primary-${item.id}`}>
                    {item.isPrimary ? (
                      <CheckCircleIcon color="primary" fontSize="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" data-testid={`hidden-${item.id}`}>
                    {item.hidden ? (
                      <CheckCircleIcon color="primary" fontSize="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const UserNavigationTab: FunctionComponent<UserNavigationTabProps> = ({
  orgId,
}) => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [resp, setResp] = useState<
    GetUserNavigationSettingsResponse | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showError } = useContext(SnackbarContext);

  useEffect(() => {
    if (!orgId) {
      console.warn("orgId is required to load user navigation settings");
      return;
    }

    setIsLoading(true);
    getUserNavigationSettings(
      { userId, orgId },
      callOptionsHeaders(regionCode, userId)
    )
      .then(setResp)
      .catch(showError)
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!resp) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No navigation configuration available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", width: 160, border: 0 }}>
                Management mode
              </TableCell>
              <TableCell sx={{ border: 0 }}>
                <Chip
                  label={managementModeLabel(resp.managementMode)}
                  color={managementModeColor(resp.managementMode)}
                  size="small"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", border: 0 }}>
                Layout type
              </TableCell>
              <TableCell sx={{ border: 0 }}>
                <Typography variant="body2">
                  {layoutTypeLabel(
                    resp.layoutSource,
                    resp.layoutSourcePermissionSetName,
                    resp.layoutSourcePermissionSetId
                  )}
                </Typography>
              </TableCell>
            </TableRow>
            {resp.updatedAt && (
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", border: 0 }}>
                  Last updated
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  <Typography variant="body2">
                    {formatDateTimeDisplay(convertTimestampToDate(resp.updatedAt!))}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {resp.updatedBy && (
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", border: 0 }}>
                  Updated by
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  <Typography variant="body2" component="span">
                    <code>{resp.updatedBy}</code>
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {renderLayoutTable("web", resp.webLayout)}
      {renderLayoutTable("mobile", resp.mobileLayout)}

      <Alert severity="info" sx={{ mt: 2 }}>
        {DISCLAIMER}
      </Alert>
    </Box>
  );
};

export default UserNavigationTab;
